import { Grid } from '@mui/material';
import { RoutalSelect, RoutalSelectMultiSelectOption } from '@nx-smartmonkey/ui';
import React, { CSSProperties } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { useHover } from 'react-use';
import styled from 'styled-components';

import More3Points from '../../../../../assets/svg/More3Points';

export interface SortableTableOptionPillItem {
  id: string;
  text: string;
  is_custom_field: boolean;
  is_required?: boolean;
}

export interface SortableTableOptionColumn {
  id: string;
  name: React.ReactElement;
  width?: string;
  formatter: ({ row }: { row: SortableTableOptionPillItem }) => React.ReactElement | string | null;
}

interface SortableTableOptionAction extends RoutalSelectMultiSelectOption {
  show?: (field: SortableTableOptionPillItem) => boolean;
}

export type SortableTableOptionActions = Array<SortableTableOptionAction | null>;

interface ContainerProps {
  isDragging?: boolean;
  isGroupedOver?: boolean;
  isHovered?: boolean;
}

const Container = styled(Grid)<ContainerProps>`
  position: relative;
  border-bottom: 1px solid #e0e0e0;
  padding: 8px;
  height: 55px;
`;
// TEMPORAL: this lines are commented until sorting is allowed. New right panel will be implemented.
// cursor: grab;
// background-color: ${(props) => {
//   if (props.isHovered) {
//     return RoutalPalette.neutral10;
//   }
//   if (props.isDragging) {
//     return RoutalPalette.neutral05;
//   }
//   return RoutalPalette.neutral00;
// }};

const getStyle = (provided: DraggableProvided, style?: CSSProperties) => {
  if (!style) {
    return provided.draggableProps.style;
  }

  return {
    ...provided.draggableProps.style,
    ...style,
  };
};

interface SortableTableOptionPillProps {
  field: SortableTableOptionPillItem;
  isDragging: boolean;
  provided: DraggableProvided;
  isGroupedOver?: boolean;
  style?: CSSProperties;
  index?: number;
  columns: Array<SortableTableOptionColumn>;
  buttonActions?: SortableTableOptionActions;
}

export const SortableTableOptionPill = ({
  field,
  isDragging,
  isGroupedOver,
  provided,
  style,
  index,
  columns,
  buttonActions,
}: SortableTableOptionPillProps) => {
  const [hoverable] = useHover((hovered: boolean) => (
    <Container
      isDragging={isDragging}
      isGroupedOver={isGroupedOver}
      isHovered={hovered}
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      style={getStyle(provided, style)}
      data-is-dragging={isDragging}
      data-testid={field.id}
      data-index={index}
      aria-label={`${field.id} field`}
      container
      direction="row"
      alignItems="center"
    >
      <Grid container direction="row" style={{ position: `relative` }} alignItems="center" wrap="nowrap">
        {/* TEMPORAL: DragIndicatorIcon is hidden until sorting is allowed. New right panel will be implemented. */}
        {/* <div style={{ width: `24px` }}>
          <DragIndicatorIcon style={{ color: RoutalPalette.secondary00 }} />
        </div> */}

        <Grid container direction="row" alignItems="center" wrap="nowrap">
          {columns.map((column) => {
            return (
              <Grid item key={column.id} style={{ width: column.width }}>
                {column.formatter({ row: field })}
              </Grid>
            );
          })}
        </Grid>

        {hovered && buttonActions && (
          <Grid item style={{ position: `absolute`, right: 0 }}>
            <RoutalSelect
              id="actions-button"
              style={{
                background: `transparent`,
                border: `none`,
                //   width: `350px`,
                // ...(popoverProps?.style ?? {}),
              }}
              hideArrow
              popoverProps={{
                options: buttonActions
                  .filter((action) => {
                    if (action === null || action.show === undefined) return true;
                    return action.show(field);
                  })
                  .map((action) => {
                    if (action === null) return null;

                    return {
                      key: action.key,
                      onClick: () => {
                        action.onClick(field);
                      },
                      content: (
                        <Grid
                          container
                          direction="row"
                          wrap="nowrap"
                          alignItems="center"
                          style={{ overflow: `hidden` }}
                        >
                          {action.content}
                        </Grid>
                      ),
                    };
                  }),
              }}
            >
              <More3Points />
            </RoutalSelect>
          </Grid>
        )}
      </Grid>
    </Container>
  ));

  return hoverable;
};
